import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ActionPayload,
  ActionsFindAllPayload,
  ActionsService,
} from "@/services/actions.service";

const ACTIONS_LIST_KEY = "pret-actions-list";
const ACTION_BY_ID_KEY = "pret-action-by-id";

export const useActions = (filters: ActionsFindAllPayload, enabled = true) => {
  const {
    data: actions,
    isLoading: isLoadingActions,
    isFetching: isFetchingActions,
    refetch: refetchActions,
  } = useQuery({
    queryKey: [ACTIONS_LIST_KEY, filters],
    queryFn: () => ActionsService.findAll(filters),
    enabled,
  });

  return {
    actions,
    isLoadingActions,
    isFetchingActions,
    refetchActions,
  };
};

export const useAction = (id: string, enabled = true) => {
  const {
    data: action,
    isLoading: isLoadingAction,
    refetch: refetchAction,
  } = useQuery({
    queryKey: [ACTION_BY_ID_KEY, id],
    queryFn: () => ActionsService.findOne(id),
    enabled: !!id && enabled,
  });

  return {
    action,
    isLoadingAction,
    refetchAction,
  };
};

export const useCreateAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ActionPayload) => ActionsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIONS_LIST_KEY] });
    },
  });
};

export const useUpdateAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ActionPayload }) =>
      ActionsService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ACTIONS_LIST_KEY] });
      queryClient.invalidateQueries({
        queryKey: [ACTION_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ActionsService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIONS_LIST_KEY] });
    },
  });
};
